import uuidv4 from './generateUUID';

function jsonTransform(srcData, targetSchema, fixedObj) {
  function getFlatJson(srcData_) {
    const context = [];
    const flatJsonObj = {};
    const arrayContext = [];

    function flatJson(resp) {
      if (Array.isArray(resp)) {
        resp.forEach((item, index) => {
          if (typeof item === 'object') {
            flatJson(item);
            if (index === resp.length - 1) {
              context.pop();
              arrayContext.pop();
            }
          } else if (index === 0) {
            const key = context.join('.');
            if (key in flatJsonObj) {
              flatJsonObj[key].push(resp.join(','));
            } else {
              flatJsonObj[key] = [resp.join(',')];
            }
            context.pop();
            arrayContext.pop();
          }
        });
      } else {
        Object.keys(resp)
          .forEach((item, index) => {
            context.push(item);
            if (Array.isArray(resp[item])) {
              arrayContext.push(item);
            }
            if (typeof resp[item] === 'object') {
              flatJson(resp[item]);
            } else {
              const key = context.join('.');
              if (key in flatJsonObj) {
                flatJsonObj[key].push(resp[item]);
              } else {
                flatJsonObj[key] = [resp[item]];
              }
              context.pop();
            }
            if ((index === Object.keys(resp).length - 1)
              && (!arrayContext.includes(context[context.length - 1]))) {
              context.pop();
            }
          });
      }
    }

    flatJson(srcData_);
    return flatJsonObj;
  }

  function* flatMap(obj) {
    const indexMap = {};
    const getProp = (prop) => {
      if (prop) {
        if (prop in indexMap) {
          indexMap[prop] += 1;
          if (obj[prop].length === indexMap[prop]) {
            indexMap[prop] = 0;
          }
        } else {
          // get the value of the last variable
          if (prop.startsWith('!')) {
            const origProp = prop.slice(1);
            return obj[origProp][indexMap[origProp]];
          }
          indexMap[prop] = 0;
        }
        if (prop === '__seq__') {
          return indexMap[prop];
        }
        if (prop === '__random__') {
          return uuidv4();
        }
        return obj[prop][indexMap[prop]];
      }
      return '';
    };
    let nextArg = yield getProp;
    while (true) {
      nextArg = yield nextArg[0](nextArg[1]);
    }
  }

  /**
   * Built-in utility function to perform basic arithmetic on variables in tagetSchema
   * In the targetSchema, this function has to be called in one the following formats
   * - 'invoke(calc, operand1, operand2, operator)'
   * - 'invoke(calc, operand1, operand2, operator) sometext' - sometext will be appended
   * to the output calc
   */
  function calc(oprnd1, oprnd2, operator) {
    let out;
    switch (operator) {
    case '+':
      out = oprnd1 + oprnd2;
      break;
    case '-':
      out = oprnd1 - oprnd2;
      break;
    case '*':
      out = oprnd1 * oprnd2;
      break;
    case '/':
      out = oprnd1 / oprnd2;
      break;
    case '%':
      out = ((oprnd1 / oprnd2) * 100).toFixed(2);
      break;
    default:
      out = oprnd1 + operator + oprnd2;
    }
    return out;
  }

  /**
   * Variable in targetSchema supports callback. This method parses the variables in the format
   * expected for a callback to work.
   * Format of this string would be 'invoke(func_to_call, variabe1, variable2)'
   * Parse the item such that, there are two tokens to work with.
   * Token 1 - function to call
   * Token 2 - target schema variable
   * Function to be called should be provided in the options object for
   * jsonTransformer method
   */
  function processExpression(variable, flatJsonObj, flatMapGen, getPropCallback) {
    const expr = /invoke\((.+?)\)(.*)/g.exec(variable);
    if (expr.length > 1) {
      const tokens = expr[1].split(',');
      const [funcName, ...vars] = tokens;
      const argsToCallback = [];
      vars.map((varName) => {
        if (varName.trim() in flatJsonObj
          || (varName.trim().startsWith('!') && varName.trim().slice(1) in flatJsonObj)) {
          const varValue = flatMapGen.next([getPropCallback, varName.trim()]).value;
          argsToCallback.push(varValue);
        } else {
          argsToCallback.push(varName.trim());
        }
        return null;
      });
      if (funcName in flatJsonObj) {
        // When there are extra characters e.g. 'invoke(calc, a, b, +) %'
        if (expr.length === 3) {
          return flatJsonObj[funcName.trim()](...argsToCallback) + expr[expr.length - 1];
        }
        return flatJsonObj[funcName.trim()](...argsToCallback);
      }
    }
    return null;
  }

  function getTargetJson(targetResp, flatJsonObj, flatMapGen, getPropCallback) {
    const multiVariableMap = {};

    /* eslint-disable no-param-reassign */
    function buildJson(out) {
      if (Array.isArray(out)) {
        out.forEach((item, index) => {
          if (typeof item === 'object') {
            buildJson(item);
          } else if (typeof item === 'string' && item.includes('|')) {
            if (!(item in multiVariableMap)) {
              multiVariableMap[item] = 0;
            }
            const key = item.split('|')[multiVariableMap[item]];
            if (key in flatJsonObj) {
              out[index] = flatMapGen.next([getPropCallback, key]).value;
              const multiVariableCounter = `${item}_${key}`;
              if (!(multiVariableCounter in multiVariableMap)) {
                multiVariableMap[multiVariableCounter] = 1;
              } else {
                multiVariableMap[multiVariableCounter] += 1;
              }

              if (flatJsonObj[key].length === multiVariableMap[multiVariableCounter]) {
                multiVariableMap[multiVariableCounter] = 0;
                multiVariableMap[item] += 1;
              }
            }
          } else if (typeof item === 'string' && item.startsWith('invoke')) {
            const temp = processExpression(item, flatJsonObj, flatMapGen, getPropCallback);
            if (temp) {
              out[index] = temp;
            }
          } else if (typeof item === 'string' && item.startsWith('!')) {
            if (item.slice(1) in flatJsonObj) {
              out[index] = flatMapGen.next([getPropCallback, item]).value;
            }
          } else if (item in flatJsonObj) {
            out[index] = flatMapGen.next([getPropCallback, item]).value;
          } else {
            //
          }
        });
      } else {
        Object.keys(out)
          .forEach((item) => {
            // If value is an object,
            // process all properties in the object
            if (typeof out[item] === 'object') {
              // If the key includes special character asterisk (*),
              // Split multiplier (after asterisk) from key name (before asterisk)
              if (item.includes('*')) {
                const multiplierKey = item.split('*')[1];
                let multiplierCount;
                // If multiplier is a key name from the source JSON, use it
                if (multiplierKey in flatJsonObj) {
                  multiplierCount = flatJsonObj[multiplierKey];
                } else {
                  // multiplier is the key name of the object in source JSON, so find occurrences
                  // of the key in the flat-json
                  multiplierCount = Object.keys(flatJsonObj)
                    .filter(key => key.includes(multiplierKey));
                }

                const objectToClone = out[item][0];
                let counter = 0;
                const clonedObj = [];
                while (counter < multiplierCount.length) {
                  clonedObj.push(JSON.parse(JSON.stringify(objectToClone)));
                  counter += 1;
                }
                delete out[item];
                item = item.split('*')[0]; // eslint-disable-line prefer-destructuring
                out[item] = clonedObj;
              }
              buildJson(out[item]);
            } else if (typeof out[item] === 'string' && out[item].includes('|')) {
              if (!(out[item] in multiVariableMap)) {
                multiVariableMap[out[item]] = 0;
              }
              const key = out[item].split('|')[multiVariableMap[out[item]]];
              if (key in flatJsonObj) {
                const curVal = flatMapGen.next([getPropCallback, key]).value;
                const multiVariableCounter = `${item}_${key}`;
                if (!(multiVariableCounter in multiVariableMap)) {
                  multiVariableMap[multiVariableCounter] = 1;
                } else {
                  multiVariableMap[multiVariableCounter] += 1;
                }

                if (flatJsonObj[key].length === multiVariableMap[multiVariableCounter]) {
                  multiVariableMap[multiVariableCounter] = 0;
                  multiVariableMap[out[item]] += 1;
                }
                out[item] = curVal;
              }
            } else if (typeof out[item] === 'string' && out[item].startsWith('invoke(')) {
              const temp = processExpression(out[item], flatJsonObj, flatMapGen, getPropCallback);
              if (temp) {
                out[item] = temp;
              }
            } else if (typeof out[item] === 'string' && out[item].startsWith('!')) {
              // Get last variable's value
              if (out[item].slice(1) in flatJsonObj) {
                out[item] = flatMapGen.next([getPropCallback, out[item]]).value;
              }
            } else if (out[item] in flatJsonObj) {
              out[item] = flatMapGen.next([getPropCallback, out[item]]).value;
            } else {
              //
            }
          });
      }
    }
    buildJson(targetResp);
  }

  /**
   * Represents a book.
   * @param {string} srcData_ - JSON that needs to be transformed into another shape
   * @param {string} targetSchema_ - JSON that describes enable transformation of srcData_
   * @param {string} fixedObj_ - Object with properties can be accessed during transformation
   * @param {string} debug - When set to true, it returns intermediate representation of
   * srcData_ along with the final output (target JSON)
   */
  function run(srcData_, targetSchema_, fixedObj_, debug = false) {
    // named the following variable as jsonTarget as this object
    // finally holds the output. At the stage, it is still targetSchema_
    const jsonTarget = JSON.parse(JSON.stringify(targetSchema_));

    // Create Flat JSON object (intermediate representation) from srcData */
    const flatJsonObj = getFlatJson(srcData_);
    // default key/value pairs that need to be added to Flat JSON object
    const builtInProps = {
      __seq__: '',
      __random__: '',
      calc,
    };
    // create a final Flat JSON from three different sources
    // - Flat JSON derived from srcData_
    // - default Flat JSON
    // - Flat JSON passed to jsonTransform in the last argument (fixedObj parameter)
    const mergedFlatJsonObj = { ...flatJsonObj, ...fixedObj_, ...builtInProps };
    // create a generator for the final Flat JSON
    const flatMapGen = flatMap(mergedFlatJsonObj);
    // get a reference to a closure defined inside generator
    // this function will be used to get value of variables defined in
    // targetSchema JSON
    const getPropCallback = flatMapGen.next().value;
    // call a method that transforms srcData_ to the final shape
    getTargetJson(jsonTarget, mergedFlatJsonObj, flatMapGen, getPropCallback);
    if (debug) {
      // used by json transformer debug tool
      return [mergedFlatJsonObj, jsonTarget];
    }
    // used by instant filters UI
    return jsonTarget;
  }
  // set debug to true when this file is used in transformer debug tool
  return run(srcData, targetSchema, fixedObj, false);
}

export default jsonTransform;
